import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSchoolSettings } from '@/context/SchoolSettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Send, Award, AlertCircle, History, Search, Users, TrendingUp, TrendingDown, Brain, ChevronRight, User, Star, Zap, CheckCircle2 } from 'lucide-react';
import MessageBell from '@/components/MessageBell';
import ProfileSettings from '@/components/ProfileSettings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Merit Plus Light Theme Colors
const colors = {
  canvas: '#F8FAFC',
  surface: '#FFFFFF',
  card: '#FFFFFF',
  cardHover: '#F1F5F9',
  teal: '#0D9488',
  tealHover: '#0F766E',
  tealLight: '#CCFBF1',
  indigo: '#4F46E5',
  indigoLight: '#E0E7FF',
  merit: '#10B981',
  meritBg: '#ECFDF5',
  demerit: '#EF4444',
  demeritBg: '#FEF2F2',
  gold: '#F59E0B',
  goldBg: '#FEF3C7',
  purple: '#8B5CF6',
  purpleBg: '#EDE9FE',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  border: '#E2E8F0',
};

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png";

export default function TeacherDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const { settings: schoolSettings } = useSchoolSettings();
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const [students, setStudents] = useState([]);
  const [behaviors, setBehaviors] = useState([]);
  const [records, setRecords] = useState([]);
  const [classList, setClassList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('');
  const [behaviorType, setBehaviorType] = useState(''); // 'merit' or 'demerit' or '' for all
  const [selectedStudents, setSelectedStudents] = useState([]); // Multiple students
  const [selectedBehaviors, setSelectedBehaviors] = useState([]); // Multiple behaviors
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    comment: '',
  });
  
  // My Class (Sınıfım) states
  const [myClasses, setMyClasses] = useState([]);
  const [selectedMyClass, setSelectedMyClass] = useState(null);
  const [myClassStudents, setMyClassStudents] = useState([]);
  const [myClassStats, setMyClassStats] = useState(null);
  const [loadingMyClass, setLoadingMyClass] = useState(false);
  const [selectedStudentHistory, setSelectedStudentHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [studentAiAnalysis, setStudentAiAnalysis] = useState(null);
  const [loadingStudentAi, setLoadingStudentAi] = useState(false);
  
  // Favorites states
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  
  // Submission state to prevent double-clicks
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    fetchMyClasses();
    fetchFavorites();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, behaviorsRes, recordsRes, classesRes] = await Promise.all([
        axios.get(`${API}/teacher/students`, { headers: getAuthHeaders() }),
        axios.get(`${API}/teacher/behaviors`, { headers: getAuthHeaders() }),
        axios.get(`${API}/teacher/records`, { headers: getAuthHeaders() }),
        axios.get(`${API}/teacher/classes`, { headers: getAuthHeaders() }),
      ]);
      setStudents(studentsRes.data);
      setBehaviors(behaviorsRes.data);
      setRecords(recordsRes.data);
      setClassList(classesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyClasses = async () => {
    try {
      const response = await axios.get(`${API}/teacher/my-classes`, { headers: getAuthHeaders() });
      setMyClasses(response.data);
      if (response.data.length > 0) {
        selectMyClass(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching my classes:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/teacher/favorites`, { headers: getAuthHeaders() });
      setFavorites(response.data.favorites || []);
      setFavoriteIds(response.data.favorite_ids || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (behaviorId) => {
    const isFavorite = favoriteIds.includes(behaviorId);
    try {
      const response = await axios.put(
        `${API}/teacher/favorites`,
        { behavior_id: behaviorId, action: isFavorite ? 'remove' : 'add' },
        { headers: getAuthHeaders() }
      );
      setFavoriteIds(response.data.favorite_ids);
      // Update favorites list
      if (isFavorite) {
        setFavorites(favorites.filter(f => f.id !== behaviorId));
      } else {
        const behavior = behaviors.find(b => b.id === behaviorId);
        if (behavior) setFavorites([...favorites, behavior]);
      }
      toast.success(isFavorite ? 'Таңдаулылардан алынды' : 'Таңдаулыларға қосылды');
    } catch (error) {
      toast.error('Қате орын алды');
    }
  };

  const quickAssignFromFavorite = (behavior) => {
    // Add to selected behaviors if not already selected
    if (!selectedBehaviors.includes(behavior.id)) {
      setSelectedBehaviors([...selectedBehaviors, behavior.id]);
    }
  };

  const selectMyClass = async (cls) => {
    setSelectedMyClass(cls);
    setLoadingMyClass(true);
    setAiAnalysis(null);
    try {
      const [studentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/teacher/my-class/${cls.id}/students`, { headers: getAuthHeaders() }),
        axios.get(`${API}/teacher/my-class/${cls.id}/stats`, { headers: getAuthHeaders() }),
      ]);
      setMyClassStudents(studentsRes.data);
      setMyClassStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching class data:', error);
      toast.error('Сынып мәліметтерін жүктеу кезінде қате орын алды');
    } finally {
      setLoadingMyClass(false);
    }
  };

  const fetchStudentHistory = async (studentId) => {
    if (!selectedMyClass) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API}/teacher/my-class/${selectedMyClass.id}/student/${studentId}/history`,
        { headers: getAuthHeaders() }
      );
      setSelectedStudentHistory(response.data);
    } catch (error) {
      console.error('Error fetching student history:', error);
      toast.error('Оқушы тарихын жүктеу кезінде қате орын алды');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchClassAiAnalysis = async () => {
    if (!selectedMyClass) return;
    setLoadingAi(true);
    try {
      const response = await axios.get(
        `${API}/teacher/my-class/${selectedMyClass.id}/ai-analysis`,
        { headers: getAuthHeaders() }
      );
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      toast.error('AI талдау қолжетімсіз');
    } finally {
      setLoadingAi(false);
    }
  };

  const fetchStudentAiAnalysis = async (studentId) => {
    if (!selectedMyClass) return;
    setLoadingStudentAi(true);
    setStudentAiAnalysis(null);
    try {
      const response = await axios.get(
        `${API}/teacher/my-class/${selectedMyClass.id}/student/${studentId}/ai-analysis`,
        { headers: getAuthHeaders() }
      );
      setStudentAiAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching student AI analysis:', error);
      toast.error('AI талдау қолжетімсіз');
    } finally {
      setLoadingStudentAi(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    if (selectedStudents.length === 0 || selectedBehaviors.length === 0) {
      toast.error('Оқушылар мен мінез-құлықты таңдаңыз');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create records for all selected students and all selected behaviors
      const promises = [];
      for (const studentId of selectedStudents) {
        for (const behaviorId of selectedBehaviors) {
          promises.push(
            axios.post(
              `${API}/teacher/records`,
              {
                student_id: studentId,
                behavior_id: behaviorId,
                comment: formData.comment || null,
              },
              { headers: getAuthHeaders() }
            )
          );
        }
      }

      await Promise.all(promises);
      
      const totalPoints = calculateSelectedBehaviorsTotal();
      toast.success(`${selectedStudents.length} оқушыға ${selectedBehaviors.length} мінез-құлық сәтті сақталды (${totalPoints >= 0 ? '+' : ''}${totalPoints} ұпай)`);
      setFormData({ comment: '' });
      setSelectedClass('');
      setSelectedStudents([]);
      setSelectedBehaviors([]);
      setBehaviorType('');
      fetchData();
    } catch (error) {
      toast.error('Қате орын алды');
      console.error('Error creating records:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle behavior selection
  const toggleBehavior = (behaviorId) => {
    setSelectedBehaviors((prev) =>
      prev.includes(behaviorId)
        ? prev.filter((id) => id !== behaviorId)
        : [...prev, behaviorId]
    );
  };

  // Calculate total points for selected behaviors
  const calculateSelectedBehaviorsTotal = () => {
    return selectedBehaviors.reduce((total, behaviorId) => {
      const behavior = behaviors.find(b => b.id === behaviorId);
      if (behavior) {
        return total + (behavior.type === 'merit' ? behavior.points : -behavior.points);
      }
      return total;
    }, 0);
  };

  const toggleStudent = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map((s) => s.id));
    }
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('school_subdomain');
    navigate(subdomain ? `/${subdomain}` : '/');
    toast.success('Сәтті шықтыңыз');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Жүктелуде...</div>
      </div>
    );
  }

  // Filter students by selected class and search term
  const filteredStudents = selectedClass
    ? students.filter((s) => 
        s.class_id === selectedClass && 
        s.full_name.toLowerCase().includes(studentSearchTerm.toLowerCase())
      )
    : [];

  // Filter behaviors by type (or show all if no type selected)
  const filteredBehaviors = behaviorType
    ? behaviors.filter((b) => b.type === behaviorType)
    : behaviors;

  const meritBehaviors = behaviors.filter((b) => b.type === 'merit');
  const demeritBehaviors = behaviors.filter((b) => b.type === 'demerit');

  // Get selected behaviors details
  const selectedBehaviorsDetails = selectedBehaviors.map(id => behaviors.find(b => b.id === id)).filter(Boolean);
  const totalSelectedPoints = calculateSelectedBehaviorsTotal();

  return (
    <div 
      className="min-h-screen bg-slate-50" 
      data-testid="teacher-dashboard"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18">
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src={schoolSettings?.logo_url || LOGO_URL}
                alt={schoolSettings?.school_name || 'Merit Plus'}
                className="w-10 h-10 sm:w-11 sm:h-11 object-contain rounded-lg shadow-sm"
              />
              <div>
                <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">
                  {schoolSettings?.school_name || 'Merit Plus'}
                </h1>
                <p className="text-xs sm:text-sm font-medium text-teal-600">
                  Мұғалім панелі
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-semibold text-slate-900">{user?.full_name}</p>
                <p className="text-xs text-slate-500">Мұғалім</p>
              </div>
              <MessageBell className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700" />
              <ProfileSettings user={user} />
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleLogout}
                className="text-slate-500 hover:text-red-600 hover:bg-red-50 border-slate-200"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Tabs defaultValue="assign" className="space-y-6">
          <TabsList 
            className={`grid w-full ${myClasses.length > 0 ? 'grid-cols-3' : 'grid-cols-2'} p-1 rounded-xl bg-white border border-slate-200`}
          >
            <TabsTrigger 
              value="assign" 
              data-testid="assign-tab" 
              className="text-xs sm:text-sm rounded-lg text-slate-600 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 transition-all"
            >
              <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Ұпай беру</span>
              <span className="sm:hidden">Беру</span>
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              data-testid="history-tab" 
              className="text-xs sm:text-sm rounded-lg data-[state=active]:text-white transition-all"
              style={{ color: colors.textSecondary }}
            >
              <History className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Тарих
            </TabsTrigger>
            {myClasses.length > 0 && (
              <TabsTrigger 
                value="myclass" 
                data-testid="myclass-tab" 
                className="text-xs sm:text-sm rounded-lg data-[state=active]:text-white transition-all"
                style={{ color: colors.textSecondary }}
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Сыныбым</span>
                <span className="sm:hidden">Сынып</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Assign Tab */}
          <TabsContent value="assign" className="space-y-6">
            <div>
              <h2 
                className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
                style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
              >
                Марапат / Ескерту беру
              </h2>
              <p className="text-sm sm:text-base" style={{ color: colors.textSecondary }}>
                Оқушыларға мінез-құлық ұпайын беріңіз
              </p>
            </div>

            {/* Favorites Quick Access */}
            {favorites.length > 0 && (
              <div 
                className="relative overflow-hidden rounded-2xl p-6"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.gold}30`
                }}
              >
                <div 
                  className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-20"
                  style={{ backgroundColor: colors.gold }}
                />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="p-2 rounded-xl"
                      style={{ backgroundColor: `${colors.gold}20` }}
                    >
                      <Star className="w-5 h-5" style={{ color: colors.gold }} fill={colors.gold} />
                    </div>
                    <h3 
                      className="text-lg font-semibold"
                      style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                    >
                      Таңдаулылар - Жылдам қолжетімділік
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {favorites.map((fav) => (
                      <Button
                        key={fav.id}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1 transition-all duration-200 hover:scale-105"
                        style={{
                          backgroundColor: selectedBehaviors.includes(fav.id) 
                            ? (fav.type === 'merit' ? colors.merit : colors.demerit)
                            : 'transparent',
                          borderColor: fav.type === 'merit' ? `${colors.merit}50` : `${colors.demerit}50`,
                          color: selectedBehaviors.includes(fav.id) ? 'white' : colors.textPrimary
                        }}
                        onClick={() => quickAssignFromFavorite(fav)}
                      >
                        <Zap className="w-3 h-3" />
                        {fav.name}
                        <span 
                          className="ml-1 px-1.5 py-0.5 rounded text-xs font-bold"
                          style={{
                            backgroundColor: selectedBehaviors.includes(fav.id) 
                              ? 'rgba(255,255,255,0.2)' 
                              : (fav.type === 'merit' ? `${colors.merit}20` : `${colors.demerit}20`),
                            color: selectedBehaviors.includes(fav.id) 
                              ? 'white' 
                              : (fav.type === 'merit' ? colors.merit : colors.demerit)
                          }}
                        >
                          {fav.type === 'merit' ? '+' : '-'}{fav.points}
                        </span>
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs mt-3 flex items-center gap-1" style={{ color: colors.textMuted }}>
                    <span 
                      className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                      style={{ backgroundColor: colors.gold }}
                    />
                    Бірнеше таңдаулыны таңдауға болады
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form */}
              <div className="lg:col-span-2">
                <div 
                  className="rounded-2xl p-6"
                  style={{ backgroundColor: colors.card, border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <h3 
                    className="text-lg font-semibold mb-4"
                    style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                  >
                    Жаңа жазба
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="assign-form">
                    {/* Step 1: Select Class */}
                    <div>
                      <Label style={{ color: colors.textSecondary }}>1. Сынып таңдаңыз</Label>
                      <Select
                        value={selectedClass}
                        onValueChange={(value) => {
                          setSelectedClass(value);
                          setFormData({ ...formData, student_id: '' });
                        }}
                      >
                        <SelectTrigger 
                          data-testid="class-select"
                          style={{ backgroundColor: colors.surface, borderColor: 'rgba(255,255,255,0.1)', color: colors.textPrimary }}
                        >
                          <SelectValue placeholder="Сынып таңдаңыз" />
                        </SelectTrigger>
                        <SelectContent style={{ backgroundColor: colors.surface, borderColor: 'rgba(255,255,255,0.1)' }}>
                          {classList.map((cls) => (
                            <SelectItem key={cls.id} value={cls.id} style={{ color: colors.textPrimary }}>
                              {cls.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Step 2: Select Students */}
                    {selectedClass && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <Label style={{ color: colors.textSecondary }}>2. Оқушыларды таңдаңыз ({filteredStudents.length})</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={selectAllStudents}
                            data-testid="select-all-button"
                            style={{ borderColor: `${colors.purple}50`, color: colors.purple }}
                          >
                              {selectedStudents.length === filteredStudents.length ? 'Барлығын алып тастау' : 'Барлығын таңдау'}
                            </Button>
                          </div>
                          {/* Student Search */}
                          <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-4 w-4" />
                            <Input
                              type="text"
                              placeholder="Оқушыны іздеу..."
                              value={studentSearchTerm}
                              onChange={(e) => setStudentSearchTerm(e.target.value)}
                              className="pl-10"
                            />
                          </div>
                          <div className="border border-border/40 rounded-lg p-4 max-h-64 overflow-y-auto scrollbar-thin space-y-2" data-testid="student-list">
                            {filteredStudents.map((student) => (
                              <label
                                key={student.id}
                                className="flex items-center space-x-3 p-3 hover:bg-secondary/50 rounded-lg cursor-pointer transition-colors"
                                data-testid={`student-checkbox-${student.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedStudents.includes(student.id)}
                                  onChange={() => toggleStudent(student.id)}
                                  className="w-5 h-5 rounded border-primary text-primary focus:ring-primary"
                                />
                                <span className="flex-1 font-medium">{student.full_name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 3: Select Behavior Type Filter (Optional) */}
                      {selectedStudents.length > 0 && (
                        <div>
                          <Label>3. Түрі бойынша сүзу (міндетті емес)</Label>
                          <div className="grid grid-cols-3 gap-2">
                            <Button
                              type="button"
                              variant={behaviorType === '' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setBehaviorType('')}
                            >
                              Барлығы
                            </Button>
                            <Button
                              type="button"
                              variant={behaviorType === 'merit' ? 'default' : 'outline'}
                              className={behaviorType === 'merit' ? 'bg-green-600 hover:bg-green-700' : ''}
                              size="sm"
                              onClick={() => setBehaviorType('merit')}
                              data-testid="merit-button"
                            >
                              <Award className="w-4 h-4 mr-1" />
                              Марапат
                            </Button>
                            <Button
                              type="button"
                              variant={behaviorType === 'demerit' ? 'default' : 'outline'}
                              className={behaviorType === 'demerit' ? 'bg-red-600 hover:bg-red-700' : ''}
                              size="sm"
                              onClick={() => setBehaviorType('demerit')}
                              data-testid="demerit-button"
                            >
                              <AlertCircle className="w-4 h-4 mr-1" />
                              Ескерту
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Step 4: Select Multiple Behaviors (Checkbox List) */}
                      {selectedStudents.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>4. Мінез-құлықтарды таңдаңыз (бірнешеуін таңдауға болады)</Label>
                            {selectedBehaviors.length > 0 && (
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setSelectedBehaviors([])}
                                className="text-xs"
                              >
                                Тазалау ({selectedBehaviors.length})
                              </Button>
                            )}
                          </div>
                          <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1">
                            {filteredBehaviors.map((behavior) => (
                              <div
                                key={behavior.id}
                                className={`flex items-center justify-between p-2 rounded-lg transition-all ${
                                  selectedBehaviors.includes(behavior.id)
                                    ? behavior.type === 'merit' 
                                      ? 'bg-green-100 border-2 border-green-500 dark:bg-green-900/30' 
                                      : 'bg-red-100 border-2 border-red-500 dark:bg-red-900/30'
                                    : 'hover:bg-secondary/50 border-2 border-transparent'
                                }`}
                                data-testid={`behavior-item-${behavior.id}`}
                              >
                                <div 
                                  className="flex items-center gap-2 flex-1 cursor-pointer"
                                  onClick={() => toggleBehavior(behavior.id)}
                                >
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                    selectedBehaviors.includes(behavior.id)
                                      ? behavior.type === 'merit' ? 'bg-green-500 border-green-500' : 'bg-red-500 border-red-500'
                                      : 'border-gray-300'
                                  }`}>
                                    {selectedBehaviors.includes(behavior.id) && (
                                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                  <div>
                                    <span className="font-medium text-sm">{behavior.name}</span>
                                    {behavior.description && (
                                      <p className="text-xs text-muted-foreground">{behavior.description}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFavorite(behavior.id);
                                    }}
                                    className="p-1 rounded hover:bg-amber-100 transition-colors"
                                    title={favoriteIds.includes(behavior.id) ? 'Таңдаулылардан алу' : 'Таңдаулыларға қосу'}
                                  >
                                    <Star className={`w-4 h-4 ${
                                      favoriteIds.includes(behavior.id) 
                                        ? 'text-amber-500 fill-amber-500' 
                                        : 'text-gray-400'
                                    }`} />
                                  </button>
                                  <span className={`font-bold text-sm px-2 py-1 rounded ${
                                    behavior.type === 'merit' 
                                      ? 'text-green-700 bg-green-100 dark:bg-green-900/50' 
                                      : 'text-red-700 bg-red-100 dark:bg-red-900/50'
                                  }`}>
                                    {behavior.type === 'merit' ? '+' : '-'}{behavior.points}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Selected Behaviors Summary */}
                      {selectedBehaviors.length > 0 && (
                        <div className="p-4 bg-secondary/30 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">Таңдалған мінез-құлықтар:</span>
                            <span className={`text-xl font-bold ${totalSelectedPoints >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {totalSelectedPoints >= 0 ? '+' : ''}{totalSelectedPoints} ұпай
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {selectedBehaviorsDetails.map((b) => (
                              <span 
                                key={b.id}
                                className={`text-xs px-2 py-1 rounded-full ${
                                  b.type === 'merit' 
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/50' 
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/50'
                                }`}
                              >
                                {b.name} ({b.type === 'merit' ? '+' : '-'}{b.points})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 5: Comment */}
                      {selectedBehaviors.length > 0 && (
                        <div>
                          <Label>5. Түсініктеме (міндетті емес)</Label>
                          <Textarea
                            value={formData.comment}
                            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                            placeholder="Қосымша түсініктеме..."
                            rows={3}
                            data-testid="comment-input"
                          />
                        </div>
                      )}

                      <Button
                        type="submit"
                        className="w-full"
                        disabled={selectedStudents.length === 0 || selectedBehaviors.length === 0 || isSubmitting}
                        data-testid="submit-button"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Жіберілуде...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            {selectedStudents.length > 0 && selectedBehaviors.length > 0
                              ? `${selectedStudents.length} оқушыға ${selectedBehaviors.length} мінез-құлық жіберу (${totalSelectedPoints >= 0 ? '+' : ''}${totalSelectedPoints})`
                              : 'Жіберу'}
                          </>
                        )}
                      </Button>
                    </form>
                  </div>
                </div>

              {/* Quick Stats */}
              <div className="space-y-4">
                <div 
                  className="rounded-2xl p-6"
                  style={{ backgroundColor: colors.card, border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3"
                      style={{ backgroundColor: `${colors.merit}20` }}
                    >
                      <Award className="w-7 h-7" style={{ color: colors.merit }} />
                    </div>
                    <div 
                      className="text-3xl font-bold"
                      style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                    >
                      {meritBehaviors.length}
                    </div>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>Марапат түрлері</p>
                  </div>
                </div>

                <div 
                  className="rounded-2xl p-6"
                  style={{ backgroundColor: colors.card, border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3"
                      style={{ backgroundColor: `${colors.demerit}20` }}
                    >
                      <AlertCircle className="w-7 h-7" style={{ color: colors.demerit }} />
                    </div>
                    <div 
                      className="text-3xl font-bold"
                      style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                    >
                      {demeritBehaviors.length}
                    </div>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>Ескерту түрлері</p>
                  </div>
                </div>

                <div 
                  className="rounded-2xl p-6"
                  style={{ backgroundColor: colors.card, border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3"
                      style={{ backgroundColor: `${colors.purple}20` }}
                    >
                      <Users className="w-7 h-7" style={{ color: colors.purple }} />
                    </div>
                    <div 
                      className="text-3xl font-bold"
                      style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                    >
                      {classList.length}
                    </div>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>Сыныптар</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <div>
              <h2 
                className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
                style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
              >
                Менің жазбаларым
              </h2>
              <p style={{ color: colors.textMuted }}>Бүгінгі айдың жазбалары</p>
            </div>

            {records.length === 0 ? (
              <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <CardContent className="py-12 text-center">
                  <p style={{ color: colors.textMuted }}>Жазбалар жоқ</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {records.map((record) => (
                  <Card 
                    key={record.id} 
                    className="border" 
                    style={{ backgroundColor: colors.card, borderColor: colors.border }}
                    data-testid={`record-${record.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            {record.behavior_type === 'merit' ? (
                              <Award className="w-5 h-5" style={{ color: colors.merit }} />
                            ) : (
                              <AlertCircle className="w-5 h-5" style={{ color: colors.demerit }} />
                            )}
                            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>{record.student_name}</h3>
                            <span 
                              className="px-2 py-1 rounded-full text-xs font-semibold"
                              style={{ 
                                backgroundColor: record.behavior_type === 'merit' ? colors.meritBg : colors.demeritBg,
                                color: record.behavior_type === 'merit' ? colors.merit : colors.demerit
                              }}
                            >
                              {record.behavior_name}
                            </span>
                          </div>
                          {record.comment && (
                            <p 
                              className="text-sm p-3 rounded-lg mt-2"
                              style={{ backgroundColor: colors.surface, color: colors.textSecondary, border: `1px solid ${colors.border}` }}
                            >
                              {record.comment}
                            </p>
                          )}
                          <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                            {format(new Date(record.created_at), 'dd MMM yyyy, HH:mm')}
                          </p>
                        </div>
                        <div className="text-right">
                          <div 
                            className="text-2xl font-bold"
                            style={{ fontFamily: "'Outfit', sans-serif", color: record.behavior_type === 'merit' ? colors.merit : colors.demerit }}
                          >
                            {record.behavior_type === 'merit' ? '+' : '-'}
                            {record.points}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Class Tab (Sınıfım) */}
          {myClasses.length > 0 && (
            <TabsContent value="myclass" className="space-y-6">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 
                      className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
                      style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                    >
                      Менің сыныбым
                    </h2>
                    <p className="text-sm sm:text-base" style={{ color: colors.textMuted }}>Сынып жетекшісі ретінде сіздің сыныбыңыз</p>
                  </div>
                  
                  {/* Class selector if multiple classes */}
                  {myClasses.length > 1 && (
                    <Select
                      value={selectedMyClass?.id}
                      onValueChange={(value) => {
                        const cls = myClasses.find(c => c.id === value);
                        if (cls) selectMyClass(cls);
                      }}
                    >
                      <SelectTrigger className="w-[180px]" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
                        <SelectValue placeholder="Сынып таңдаңыз" />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
                        {myClasses.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {loadingMyClass ? (
                  <div className="text-center py-12">
                    <div className="text-lg" style={{ color: colors.textMuted }}>Жүктелуде...</div>
                  </div>
                ) : selectedMyClass && myClassStats ? (
                  <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                        <CardContent className="p-4 text-center">
                          <Users className="w-8 h-8 mx-auto mb-2" style={{ color: colors.teal }} />
                          <div className="text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}>{myClassStats.student_count}</div>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Оқушы саны</p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                        <CardContent className="p-4 text-center">
                          <TrendingUp className="w-8 h-8 mx-auto mb-2" style={{ color: colors.merit }} />
                          <div className="text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: colors.merit }}>{myClassStats.total_merits}</div>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Марапаттар</p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                        <CardContent className="p-4 text-center">
                          <TrendingDown className="w-8 h-8 mx-auto mb-2" style={{ color: colors.demerit }} />
                          <div className="text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: colors.demerit }}>{myClassStats.total_demerits}</div>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Ескертулер</p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                        <CardContent className="p-4 text-center">
                          <Award className="w-8 h-8 mx-auto mb-2" style={{ color: colors.gold }} />
                          <div 
                            className="text-3xl font-bold" 
                            style={{ fontFamily: "'Outfit', sans-serif", color: myClassStats.average_score >= 0 ? colors.merit : colors.demerit }}
                          >
                            {myClassStats.average_score >= 0 ? '+' : ''}{myClassStats.average_score}
                          </div>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Орташа ұпай</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* AI Analysis Button */}
                    <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: colors.textPrimary }}>
                            <Brain className="w-5 h-5" style={{ color: colors.purple }} />
                            AI Талдау (Тәрбие анализі)
                          </CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={fetchClassAiAnalysis}
                            disabled={loadingAi}
                            className="hover:bg-purple-50"
                            style={{ borderColor: colors.border, color: colors.purple }}
                          >
                            {loadingAi ? 'Жүктелуде...' : aiAnalysis ? 'Қайта талдау' : 'Талдау жасау'}
                          </Button>
                        </div>
                      </CardHeader>
                      {aiAnalysis && (
                        <CardContent>
                          <div 
                            className="rounded-lg p-4 text-sm whitespace-pre-wrap"
                            style={{ backgroundColor: colors.purpleBg, color: colors.textPrimary }}
                          >
                            {aiAnalysis}
                          </div>
                        </CardContent>
                      )}
                    </Card>

                    {/* Top & Bottom Students */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Top Students */}
                      <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: colors.textPrimary }}>
                            <TrendingUp className="w-5 h-5" style={{ color: colors.merit }} />
                            Үздік оқушылар
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {myClassStats.top_students.map((student, index) => (
                              <div 
                                key={index} 
                                className="flex items-center justify-between p-2 rounded-lg transition-colors"
                                style={{ ':hover': { backgroundColor: colors.cardHover } }}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                                    index === 0 ? 'bg-amber-500 text-white' :
                                    index === 1 ? 'bg-gray-400 text-white' :
                                    index === 2 ? 'bg-orange-600 text-white' : ''
                                  }`} style={index > 2 ? { backgroundColor: colors.border, color: colors.textPrimary } : {}}>
                                    {index + 1}
                                  </div>
                                  <span className="font-medium" style={{ color: colors.textPrimary }}>{student.name}</span>
                                </div>
                                <span className="font-bold" style={{ color: colors.merit }}>+{student.score}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Students Needing Attention */}
                      <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: colors.textPrimary }}>
                            <AlertCircle className="w-5 h-5" style={{ color: colors.gold }} />
                            Назар аударуды қажет етеді
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {myClassStats.bottom_students.map((student, index) => (
                              <div key={index} className="flex items-center justify-between p-2 rounded-lg transition-colors">
                                <div className="flex items-center gap-3">
                                  <div className="w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-orange-600" />
                                  </div>
                                  <span className="font-medium">{student.name}</span>
                                </div>
                                <span className={`font-bold ${student.score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {student.score >= 0 ? '+' : ''}{student.score}
                                </span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* All Students List */}
                    <Card className="border" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg" style={{ color: colors.textPrimary }}>
                          <Users className="w-5 h-5" style={{ color: colors.teal }} />
                          Барлық оқушылар ({myClassStudents.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto">
                          {myClassStudents.map((student, index) => (
                            <Dialog key={student.student_id}>
                              <DialogTrigger asChild>
                                <div 
                                  className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors hover:bg-slate-50"
                                  style={{ border: `1px solid ${colors.border}` }}
                                  onClick={() => fetchStudentHistory(student.student_id)}
                                >
                                  <div className="flex items-center gap-3">
                                    <div 
                                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                                      style={{ backgroundColor: colors.tealLight, color: colors.teal }}
                                    >
                                      {index + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium" style={{ color: colors.textPrimary }}>{student.student_name}</p>
                                      <p className="text-xs" style={{ color: colors.textMuted }}>@{student.username}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-right">
                                      <p className="font-bold" style={{ color: student.total_score >= 0 ? colors.merit : colors.demerit }}>
                                        {student.total_score >= 0 ? '+' : ''}{student.total_score}
                                      </p>
                                      <p className="text-xs" style={{ color: colors.textMuted }}>
                                        <span style={{ color: colors.merit }}>{student.merit_count}M</span> / 
                                        <span style={{ color: colors.demerit }}>{student.demerit_count}D</span>
                                      </p>
                                    </div>
                                    <ChevronRight className="w-4 h-4" style={{ color: colors.textMuted }} />
                                  </div>
                                </div>
                              </DialogTrigger>
                              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" style={{ backgroundColor: colors.surface }}>
                                <DialogHeader>
                                  <DialogTitle style={{ color: colors.textPrimary }}>{selectedStudentHistory?.student_name || student.student_name}</DialogTitle>
                                </DialogHeader>
                                
                                {/* AI Analysis Button */}
                                <div className="border-b pb-4 mb-4" style={{ borderColor: colors.border }}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Brain className="w-5 h-5" style={{ color: colors.purple }} />
                                      <span className="font-medium" style={{ color: colors.textPrimary }}>AI Талдау</span>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => fetchStudentAiAnalysis(student.student_id)}
                                      disabled={loadingStudentAi}
                                    >
                                      {loadingStudentAi ? 'Жүктелуде...' : 'Талдау жасау'}
                                    </Button>
                                  </div>
                                  {studentAiAnalysis && studentAiAnalysis.student_name === student.student_name && (
                                    <div 
                                      className="mt-3 rounded-lg p-3 text-sm whitespace-pre-wrap"
                                      style={{ backgroundColor: colors.purpleBg, color: colors.textPrimary }}
                                    >
                                      {studentAiAnalysis.analysis}
                                    </div>
                                  )}
                                </div>

                                {/* Records History */}
                                <div>
                                  <p className="font-medium mb-3 flex items-center gap-2" style={{ color: colors.textPrimary }}>
                                    <History className="w-4 h-4" />
                                    Жазбалар тарихы
                                  </p>
                                  {loadingHistory ? (
                                    <div className="text-center py-8" style={{ color: colors.textMuted }}>Жүктелуде...</div>
                                  ) : selectedStudentHistory ? (
                                    <div className="space-y-3">
                                      {selectedStudentHistory.records.length === 0 ? (
                                        <p className="text-center py-4" style={{ color: colors.textMuted }}>Бұл айда жазба жоқ</p>
                                      ) : (
                                        selectedStudentHistory.records.map((record) => (
                                          <div 
                                            key={record.id} 
                                            className="p-3 rounded-lg border"
                                            style={{
                                              backgroundColor: record.behavior_type === 'merit' ? colors.meritBg : colors.demeritBg,
                                              borderColor: record.behavior_type === 'merit' ? '#A7F3D0' : '#FECACA'
                                            }}
                                          >
                                            <div className="flex justify-between items-start">
                                              <div>
                                                <p className="font-medium" style={{ color: colors.textPrimary }}>{record.behavior_name}</p>
                                                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{record.teacher_name}</p>
                                                {record.comment && (
                                                  <p className="text-sm mt-1 italic" style={{ color: colors.textSecondary }}>&quot;{record.comment}&quot;</p>
                                                )}
                                              </div>
                                              <div className="text-right">
                                                <span className="font-bold" style={{ color: record.behavior_type === 'merit' ? colors.merit : colors.demerit }}>
                                                  {record.behavior_type === 'merit' ? '+' : '-'}{record.points}
                                                </span>
                                                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                                                  {format(new Date(record.created_at), 'dd.MM.yyyy HH:mm')}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              </DialogContent>
                            </Dialog>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textMuted }} />
                    <p className="text-lg" style={{ color: colors.textMuted }}>Сынып таңдаңыз</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
